import styled from "@emotion/styled";
import {
  PauseRounded,
  PlayArrowRounded,
  VolumeDown,
  VolumeMute,
  VolumeUp,
} from "@mui/icons-material";
import { IconButton, Slider, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { Radius } from "Infrastructure/Themes/Themes";

type Props = {
  src: string;
  isPlaying?: boolean;
  onPlay?: () => void;
};

const Wrapper = styled("div")`
  background-color: ${(props) => props.theme.colors.backgroundInput};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${Radius.M};
  padding: ${(props) => props.theme.spacing(1, 1.5)};
`;

const TimeLabel = styled(Typography)`
  font-size: 11px;
  color: ${(props) => props.theme.palette.text.secondary};
  min-width: 36px;
  text-align: center;
  user-select: none;
`;

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const AudioPlayer = ({
  src,
  isPlaying: isPlayingProp,
  onPlay,
}: Props): React.ReactNode => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);

  useEffect(() => {
    if (isPlayingProp === false && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isPlayingProp, isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      onPlay?.();
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    const newTime = value as number;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const newVolume = value as number;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const VolumeIcon =
    volume === 0 ? VolumeMute : volume < 0.5 ? VolumeDown : VolumeUp;

  return (
    <Wrapper>
      {/* biome-ignore lint/a11y/useMediaCaption: audio player for voiceover clips does not need captions */}
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={togglePlay} size="small" color="primary">
          {isPlaying ? (
            <PauseRounded fontSize="small" />
          ) : (
            <PlayArrowRounded fontSize="small" />
          )}
        </IconButton>

        <TimeLabel>{formatTime(currentTime)}</TimeLabel>

        <Slider
          size="small"
          value={currentTime}
          max={duration || 1}
          onChange={handleSeek}
          sx={{
            flex: 1,
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 12, height: 12 },
            "& .MuiSlider-rail": { opacity: 0.3 },
          }}
        />

        <TimeLabel>{formatTime(duration)}</TimeLabel>

        <VolumeIcon sx={{ fontSize: 16, color: "text.secondary", ml: 0.5 }} />
        <Slider
          size="small"
          value={volume}
          min={0}
          max={1}
          step={0.05}
          onChange={handleVolumeChange}
          sx={{
            width: 60,
            color: "primary.main",
            "& .MuiSlider-thumb": { width: 10, height: 10 },
            "& .MuiSlider-rail": { opacity: 0.3 },
          }}
        />
      </Stack>
    </Wrapper>
  );
};
